import type { ComposeRewriteAction, ComposeTone } from '@fieldops/shared'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import { errorMessage, useTRPC, type RouterOutputs } from '../../lib/trpc.js'
import type { TextRange } from './MessageBody.js'
import { joinQuote, splitQuote } from './text.js'
import type { TextHistory } from './useTextHistory.js'

export interface ComposeTarget {
  channel: 'EMAIL' | 'SMS'
  jobId?: string | null
  replyToMessageId?: string | null
  document?: { kind: 'quote' | 'invoice'; id: string } | null
}

export interface MessageValue {
  subject: string
  body: string
}

export type ComposeContextData = RouterOutputs['compose']['context']

interface DraftInput {
  intents: string[]
  instruction: string | null
  tone: ComposeTone
  referenceIds: string[]
  current: MessageValue | null
}

/**
 * Everything the Assistant does to one message.
 *
 * It only ever changes the unsent words on screen, through the window's own
 * history, so the banner's Undo and Cmd+Z both take a draft back. What a
 * suggestion says is re-derived by the server from the database; this sends
 * its key. A reply's quoted message is kept out of what the model sees as
 * the draft, and put back under whatever it writes. Decision 95.
 */
export function useComposeAssistant({
  target,
  history,
  recipientFirstName,
  enabled = true,
  autoDraft = true,
  onAttach,
  onRange,
}: {
  target: ComposeTarget
  history: TextHistory<MessageValue>
  /** Read when drafting, since who the message is to can change while it is open. */
  recipientFirstName: () => string | null
  enabled?: boolean
  /** Off when reopening a message already written, such as a scheduled one. */
  autoDraft?: boolean
  onAttach: (ids: string[]) => void
  onRange: (range: TextRange | null) => void
}) {
  const trpc = useTRPC()
  const input = {
    channel: target.channel,
    jobId: target.jobId ?? null,
    replyToMessageId: target.replyToMessageId ?? null,
    document: target.document ?? null,
  }
  const context = useQuery({
    ...trpc.compose.context.queryOptions(input),
    enabled,
    staleTime: 60_000,
    retry: false,
  })
  const drafting = useMutation(trpc.compose.draft.mutationOptions())
  const rewriting = useMutation(trpc.compose.rewrite.mutationOptions())

  const [selected, setSelected] = useState<string[]>([])
  const [tone, setTone] = useState<ComposeTone>(target.channel === 'SMS' ? 'BRIEF' : 'FRIENDLY')
  const [using, setUsing] = useState<string[]>([])
  const [asks, setAsks] = useState<string[]>([])
  const [drafted, setDrafted] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState<TextRange | null>(null)

  const latest = useRef(history.value)
  latest.current = history.value
  const last = useRef<DraftInput | null>(null)
  const draftedSubject = useRef<string | null>(null)
  const autoDrafted = useRef(false)

  const data = context.data
  const available = Boolean(data?.available)
  const suggestions = data?.suggestions ?? []

  const refsOf = (keys: string[]) =>
    suggestions.filter((row) => keys.includes(row.key)).flatMap((row) => row.referenceIds)

  const run = async (request: DraftInput) => {
    last.current = request
    setError(null)
    const before = latest.current
    const { quote } = splitQuote(before.body)
    try {
      const result = await drafting.mutateAsync({
        ...input,
        intents: request.intents,
        instruction: request.instruction,
        tone: request.tone,
        referenceIds: request.referenceIds,
        current: request.current,
        recipientFirstName: recipientFirstName(),
      })
      const now = latest.current
      const keepSubject =
        target.channel === 'SMS' ||
        !result.subject ||
        (now.subject.trim() !== '' && now.subject !== draftedSubject.current)
      const subject = keepSubject ? now.subject : result.subject!
      if (!keepSubject) draftedSubject.current = subject
      history.commit({ subject, body: joinQuote(result.body, quote) })
      onRange(null)
      if (result.attachmentIds.length) onAttach(result.attachmentIds)
      // What the model actually read, which is what Using should say.
      setUsing(result.referenceIds)
      setDrafted(true)
    } catch (failure) {
      setError(errorMessage(failure))
    }
  }

  const currentOwn = (): MessageValue | null => {
    const { own } = splitQuote(latest.current.body)
    return own.trim() ? { subject: latest.current.subject, body: own } : null
  }

  const toggle = (key: string) => {
    const on = selected.includes(key)
    const next = on ? selected.filter((item) => item !== key) : [...selected, key]
    setSelected(next)
    const mine = refsOf([key])
    const others = new Set(refsOf(next.filter((item) => item !== key)))
    const nextUsing = on
      ? using.filter((id) => !mine.includes(id) || others.has(id))
      : [...using, ...mine.filter((id) => !using.includes(id))]
    setUsing(nextUsing)
    // The first tick writes at once; more ticks wait for Combine.
    if (!on && selected.length === 0 && available) {
      void run({
        intents: [key],
        instruction: null,
        tone,
        referenceIds: nextUsing,
        current: null,
      })
    }
  }

  const combine = () => {
    if (selected.length === 0) return
    void run({
      intents: selected,
      instruction: null,
      tone,
      referenceIds: using,
      current: null,
    })
  }

  const changeTone = (next: ComposeTone) => {
    setTone(next)
    const current = currentOwn()
    if (!available || (!current && selected.length === 0)) return
    void run({
      intents: selected,
      instruction: null,
      tone: next,
      referenceIds: using,
      current,
    })
  }

  const ask = (instruction: string) => {
    const words = instruction.trim()
    if (!words) return
    setAsks((list) => [...list, words].slice(-3))
    void run({
      intents: selected,
      instruction: words,
      tone,
      referenceIds: using,
      current: currentOwn(),
    })
  }

  const redoDraft = () => {
    if (last.current) void run(last.current)
  }

  const undoDraft = () => {
    history.undo()
    setDrafted(false)
  }

  const addReference = (id: string) => setUsing((list) => (list.includes(id) ? list : [...list, id]))
  const removeReference = (id: string) => setUsing((list) => list.filter((item) => item !== id))

  const rewrite = async (action: ComposeRewriteAction, range: TextRange) => {
    const body = latest.current.body
    const text = body.slice(range.start, range.end)
    if (!text.trim()) return
    setBusy(range)
    setError(null)
    try {
      const result = await rewriting.mutateAsync({
        ...input,
        action,
        text: text.trim(),
        surrounding: body,
      })
      const now = latest.current
      if (now.body.slice(range.start, range.end) !== text) {
        setError('The words changed while the Assistant was rewriting them, so nothing was replaced.')
        return
      }
      const lead = /^\s*/.exec(text)![0]
      const trail = /\s*$/.exec(text)![0]
      const replacement = `${lead}${result.text}${trail}`
      history.commit({
        ...now,
        body: `${now.body.slice(0, range.start)}${replacement}${now.body.slice(range.end)}`,
      })
      onRange({
        start: range.start + lead.length,
        end: range.start + lead.length + result.text.length,
      })
    } catch (failure) {
      setError(errorMessage(failure))
    } finally {
      setBusy(null)
    }
  }

  // One draft of the top suggestion when the window opens on an empty message.
  useEffect(() => {
    if (autoDrafted.current || !data?.available) return
    autoDrafted.current = true
    if (!autoDraft) return
    const top = data.suggestions[0]
    if (!top || currentOwn()) return
    toggle(top.key)
  }, [data])

  return {
    context,
    data,
    available,
    suggestions,
    selected,
    tone,
    using,
    asks,
    drafted,
    drafting: drafting.isPending,
    busy,
    error,
    clearError: () => setError(null),
    toggle,
    combine,
    changeTone,
    ask,
    redoDraft,
    undoDraft,
    addReference,
    removeReference,
    rewrite,
  }
}

export type ComposeAssistant = ReturnType<typeof useComposeAssistant>
